/* eslint-disable react/jsx-no-useless-fragment */
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import "react-phone-number-input/style.css";
import { Country } from "react-phone-number-input/input";
import { ICaret, TextField, VStack } from "../../../components";
import { Flag, IAddress, ICustomer } from "../../../types";
import { CustomerInformationFormSchema } from "../../../utils/Validation";
import styles from "./index.module.css";
import ContactNumberInput from "../../../components/ContactNumberInput/ContactNumberInput";
import useGooglePlaces from "../../../hooks/useGooglePlaces";
import useComponentVisible from "../../../hooks/useComponentVisible";
import { setSessionStorageData, StorageKeys } from "../../../utils/Storage";

type CustomerData = {
  customerInformation: ICustomer;
};
type CustomerAddressData = {
  customerInformation: {
    billingAddress: IAddress;
  };
};

export interface CustomerInputFormProps {
  canProceed: any;
  data: any;
  // eslint-disable-next-line no-unused-vars
  updateCustomerData: (fields: Partial<CustomerData>) => void;
  // eslint-disable-next-line no-unused-vars
  updateAddressData: (fields: Partial<CustomerAddressData>) => void;
}

const CustomerInputForm = ({ canProceed, data, updateCustomerData, updateAddressData }: CustomerInputFormProps) => {
  const { customerInformation } = data;
  const { billingAddress } = customerInformation;
  const [openPanel, setOpenPanel] = useState(false);

  const customerInformationForm = useFormik<ICustomer>({
    initialValues: {
      customerName: customerInformation?.customerName || "",
      emailAddress: customerInformation?.emailAddress || "",
      contactNumber: customerInformation?.contactNumber || "",
      billingAddress: {
        country: customerInformation?.billingAddress?.country || "",
        complexBuilding: customerInformation?.billingAddress?.complexBuilding || "",
        streetAddress: customerInformation?.billingAddress?.streetAddress || "",
        suburb: customerInformation?.billingAddress?.suburb || "",
        city: customerInformation?.billingAddress?.city || "",
        province: customerInformation?.billingAddress?.province || "",
        postalCode: customerInformation?.billingAddress?.postalCode || "",
      },
    },
    validationSchema: CustomerInformationFormSchema,
    onSubmit: () => {},
  });

  const [selectedPlaceId, setSelectedPlaceId] = useState<string | undefined>(undefined);
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
  const isNewSearch = useRef<boolean>(true);
  const { predictions, selectedPlace } = useGooglePlaces(
    customerInformationForm.values.billingAddress.streetAddress ?? "",
    selectedPlaceId,
    isNewSearch.current,
  );

  useEffect(() => {
    if (selectedPlace && selectedPlaceId) {
      setSelectedPlaceId(undefined);
      isNewSearch.current = false;
      const { streetAddress, city, suburb, province, country, postalCode } = selectedPlace;
      customerInformationForm.values.billingAddress.streetAddress = streetAddress;
      customerInformationForm.values.billingAddress.suburb = suburb;
      customerInformationForm.values.billingAddress.city = city;
      customerInformationForm.values.billingAddress.province = province;
      customerInformationForm.values.billingAddress.country = country;
      customerInformationForm.values.billingAddress.postalCode = postalCode;
      updateAddressData({
        customerInformation: {
          ...customerInformation,
          billingAddress: {
            ...billingAddress,
            streetAddress,
            suburb,
            city,
            province,
            country,
            postalCode,
          },
        },
      });
      customerInformationForm.setFormikState((prevState) => ({ ...prevState, dirty: true }));
      customerInformationForm.validateForm();
    }
  }, [selectedPlace]);

  useEffect(() => {
    setIsComponentVisible(predictions?.length > 0);
  }, [predictions]);

  canProceed(false); // default to false
  if (customerInformationForm.isValid) canProceed(true); // only update if valid
  useEffect(() => {
    customerInformationForm.validateForm(); // force validation check on back button and refresh
    if (customerInformationForm.isValid) canProceed(true);
  }, []);

  const handleUpdateCustomerData = (e: React.ChangeEvent<HTMLInputElement>) => {
    customerInformationForm.handleChange(e);
    updateCustomerData({
      customerInformation: {
        ...customerInformation,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleUpdateCustomerAddressData = (e: React.ChangeEvent<HTMLInputElement>) => {
    customerInformationForm.handleChange(e);
    isNewSearch.current = true;
    updateAddressData({
      customerInformation: {
        ...customerInformation,
        billingAddress: {
          ...billingAddress,
          [e.target.name.split(".")[1]]: e.target.value,
        },
      },
    });
  };

  const onContactChange = (number: string, _flagIcon: Flag, _countryCode: string, _country: string, _ISO: Country): string => {
    customerInformation.contactNumber = number;
    updateCustomerData({
      customerInformation: {
        ...customerInformation,
        contactNumber: number,
      },
    });
    setSessionStorageData(StorageKeys.CUSTOMER_CONTACT, JSON.stringify({ functions: _flagIcon, _countryCode, _country, _ISO }));
    return customerInformation.contactNumber;
  };

  const billingAddressInfoRequired =
    (customerInformationForm.values.billingAddress?.streetAddress ?? "")?.length > 0 ||
    (customerInformationForm.values.billingAddress?.suburb ?? "")?.length > 0 ||
    (customerInformationForm.values.billingAddress?.complexBuilding ?? "")?.length > 0 ||
    (customerInformationForm.values.billingAddress?.province ?? "")?.length > 0 ||
    (customerInformationForm.values.billingAddress?.city ?? "")?.length > 0 ||
    (customerInformationForm.values.billingAddress?.country ?? "")?.length > 0 ||
    (customerInformationForm.values.billingAddress?.postalCode ?? "")?.length > 0;

  return (
    <>
      <VStack className="d-flex justify-content-center direction-row px-0">
        <div className={styles.Content}>
          <TextField
            className={
              customerInformationForm.touched &&
              customerInformationForm.touched.customerName &&
              customerInformationForm.errors.customerName &&
              "border-red"
            }
            label="Customer name"
            name="customerName"
            type="text"
            placeholder=""
            required
            minLength={2}
            maxLength={40}
            showRemainingCharacters
            value={customerInformationForm.values.customerName}
            onChange={handleUpdateCustomerData}
            onBlur={customerInformationForm.handleBlur("customerName")}
            errorMessage={
              customerInformationForm.touched &&
              customerInformationForm.touched.customerName &&
              customerInformationForm.errors.customerName
            }
            status={
              !customerInformationForm.errors.customerName &&
              customerInformationForm.values.customerName.length >= 3 &&
              customerInformationForm.values.customerName.length <= 71 &&
              "complete"
            }
          />
          <div className={styles.InformationTip}>
            To share your invoice later, we require an email and/or contact number, as you may not always have complete customer
            information.
          </div>
          <TextField
            className={
              customerInformationForm.touched &&
              customerInformationForm.touched.emailAddress &&
              customerInformationForm.errors.emailAddress &&
              "border-red"
            }
            label="Email address"
            name="emailAddress"
            type="email"
            placeholder="customer@email.com"
            required
            maxLength={50}
            showRemainingCharacters
            value={customerInformationForm.values.emailAddress}
            onChange={handleUpdateCustomerData}
            onBlur={customerInformationForm.handleBlur("emailAddress")}
            errorMessage={
              customerInformationForm.touched &&
              customerInformationForm.touched.emailAddress &&
              customerInformationForm.errors.emailAddress
            }
            status={
              !customerInformationForm.errors.emailAddress &&
              customerInformationForm.values.emailAddress.length >= 7 &&
              customerInformationForm.values.emailAddress.length <= 60 &&
              "complete"
            }
          />
          <div className="mt-4">
            <ContactNumberInput value={customerInformation.contactNumber} onChange={onContactChange} />
          </div>
          <div className={styles.DropdownLabel}>
            <div role="button" onKeyDown={() => setOpenPanel(!openPanel)} onClick={() => setOpenPanel(!openPanel)} tabIndex={0}>
              <h3>Show optional information</h3>
              <ICaret className={openPanel ? styles.up : styles.down} />
            </div>
          </div>
          <div className={`${styles.OptionalFields} ${openPanel ? styles.Expanded : styles.Collapse}`}>
            <TextField
              className={`
                ${
                  customerInformationForm.touched &&
                  customerInformationForm.touched.billingAddress?.streetAddress &&
                  customerInformationForm.errors.billingAddress?.streetAddress &&
                  "border-red"
                } mb-4`}
              label="Street address"
              name="billingAddress.streetAddress"
              type="text"
              placeholder=""
              required={billingAddressInfoRequired}
              maxLength={60}
              value={customerInformationForm.values.billingAddress.streetAddress}
              onChange={handleUpdateCustomerAddressData}
              onBlur={() => customerInformationForm.handleBlur("billingAddress.streetAddress")}
              errorMessage={
                customerInformationForm.touched &&
                customerInformationForm.touched.billingAddress?.streetAddress &&
                customerInformationForm.errors.billingAddress?.streetAddress
              }
              status={
                !customerInformationForm.errors.billingAddress?.streetAddress &&
                customerInformationForm.values.billingAddress.streetAddress &&
                customerInformationForm.values.billingAddress.streetAddress.length >= 1 &&
                customerInformationForm.values.billingAddress.streetAddress.length <= 60 &&
                "complete"
              }
            />
            {predictions.length > 0 && isComponentVisible && (
              <div className={styles.ListContainer} ref={ref}>
                {predictions.slice(0, 6).map((prediction) => (
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                  <div
                    role="listitem"
                    onKeyDown={() => {}}
                    className={styles.ListItem}
                    key={prediction.place_id}
                    onClick={() => {
                      setSelectedPlaceId(prediction.place_id);
                      setIsComponentVisible(false);
                    }}
                  >
                    {prediction.description}
                  </div>
                ))}
              </div>
            )}
            <TextField
              className={`
                ${
                  customerInformationForm.touched &&
                  customerInformationForm.touched.billingAddress?.suburb &&
                  customerInformationForm.errors.billingAddress?.suburb &&
                  "border-red"
                } mb-4`}
              label="Suburb"
              name="billingAddress.suburb"
              type="text"
              placeholder=""
              maxLength={60}
              required={billingAddressInfoRequired}
              value={customerInformationForm.values.billingAddress.suburb}
              onChange={handleUpdateCustomerAddressData}
              onBlur={customerInformationForm.handleBlur("billingAddress.suburb")}
              errorMessage={
                customerInformationForm.touched &&
                customerInformationForm.touched.billingAddress?.suburb &&
                customerInformationForm.errors.billingAddress?.suburb
              }
              status={
                !customerInformationForm.errors.billingAddress?.suburb &&
                customerInformationForm.values.billingAddress.suburb &&
                customerInformationForm.values.billingAddress.suburb.length >= 1 &&
                customerInformationForm.values.billingAddress.suburb.length <= 60 &&
                "complete"
              }
            />
            <TextField
              className={`
                ${
                  customerInformationForm.touched &&
                  customerInformationForm.touched.billingAddress?.complexBuilding &&
                  customerInformationForm.errors.billingAddress?.complexBuilding &&
                  "border-red"
                } mb-4`}
              label="Complex / Building name (optional)"
              name="billingAddress.complexBuilding"
              type="text"
              placeholder=""
              maxLength={60}
              value={customerInformationForm.values.billingAddress.complexBuilding}
              onChange={handleUpdateCustomerAddressData}
              onBlur={customerInformationForm.handleBlur("billingAddress.complexBuilding")}
              errorMessage={customerInformationForm.touched && customerInformationForm.errors.billingAddress?.complexBuilding}
              status={
                !customerInformationForm.errors.billingAddress?.complexBuilding &&
                customerInformationForm.values.billingAddress.complexBuilding &&
                customerInformationForm.values.billingAddress.complexBuilding.length <= 60 &&
                "complete"
              }
            />
            <TextField
              className={`
                ${
                  customerInformationForm.touched &&
                  customerInformationForm.touched.billingAddress?.province &&
                  customerInformationForm.errors.billingAddress?.province &&
                  "border-red"
                } mb-4`}
              label="Province"
              name="billingAddress.province"
              type="text"
              placeholder=""
              required={billingAddressInfoRequired}
              maxLength={60}
              value={customerInformationForm.values.billingAddress.province}
              onChange={handleUpdateCustomerAddressData}
              onBlur={customerInformationForm.handleBlur("billingAddress.province")}
              errorMessage={
                customerInformationForm.touched &&
                customerInformationForm.touched.billingAddress?.province &&
                customerInformationForm.errors.billingAddress?.province
              }
              status={
                !customerInformationForm.errors.billingAddress?.province &&
                customerInformationForm.values.billingAddress.province &&
                customerInformationForm.values.billingAddress.province.length >= 1 &&
                customerInformationForm.values.billingAddress.province.length <= 60 &&
                "complete"
              }
            />
            <TextField
              className={`
                ${
                  customerInformationForm.touched &&
                  customerInformationForm.touched.billingAddress?.city &&
                  customerInformationForm.errors.billingAddress?.city &&
                  "border-red"
                } mb-4`}
              label="City"
              name="billingAddress.city"
              type="text"
              placeholder=""
              required={billingAddressInfoRequired}
              maxLength={60}
              value={customerInformationForm.values.billingAddress.city}
              onChange={handleUpdateCustomerAddressData}
              onBlur={customerInformationForm.handleBlur("billingAddress.city")}
              errorMessage={
                customerInformationForm.touched &&
                customerInformationForm.touched.billingAddress?.city &&
                customerInformationForm.errors.billingAddress?.city
              }
              status={
                !customerInformationForm.errors.billingAddress?.city &&
                customerInformationForm.values.billingAddress.city &&
                customerInformationForm.values.billingAddress.city.length >= 1 &&
                customerInformationForm.values.billingAddress.city.length <= 60 &&
                "complete"
              }
            />
            <TextField
              className={`
                ${
                  customerInformationForm.touched &&
                  customerInformationForm.touched.billingAddress?.country &&
                  customerInformationForm.errors.billingAddress?.country &&
                  "border-red"
                } mb-4`}
              label="Country"
              name="billingAddress.country"
              type="text"
              placeholder=""
              required={billingAddressInfoRequired}
              maxLength={60}
              value={customerInformationForm.values.billingAddress.country}
              onChange={handleUpdateCustomerAddressData}
              onBlur={customerInformationForm.handleBlur("billingAddress.country")}
              errorMessage={
                customerInformationForm.touched &&
                customerInformationForm.touched.billingAddress?.country &&
                customerInformationForm.errors.billingAddress?.country
              }
              status={
                !customerInformationForm.errors.billingAddress?.country &&
                customerInformationForm.values.billingAddress.country &&
                customerInformationForm.values.billingAddress.country.length >= 1 &&
                customerInformationForm.values.billingAddress.country.length <= 60 &&
                "complete"
              }
            />
            <TextField
              className={`
                ${
                  customerInformationForm.touched &&
                  customerInformationForm.touched.billingAddress?.postalCode &&
                  customerInformationForm.errors.billingAddress?.postalCode &&
                  "border-red"
                } mb-4`}
              label="Zip code"
              name="billingAddress.postalCode"
              type="text"
              placeholder=""
              required={billingAddressInfoRequired}
              maxLength={60}
              value={customerInformationForm.values.billingAddress.postalCode}
              onChange={handleUpdateCustomerAddressData}
              onBlur={customerInformationForm.handleBlur("billingAddress.postalCode")}
              errorMessage={
                customerInformationForm.touched &&
                customerInformationForm.touched.billingAddress?.postalCode &&
                customerInformationForm.errors.billingAddress?.postalCode
              }
              status={
                !customerInformationForm.errors.billingAddress?.postalCode &&
                customerInformationForm.values.billingAddress.postalCode &&
                customerInformationForm.values.billingAddress.postalCode.length >= 1 &&
                customerInformationForm.values.billingAddress.postalCode.length <= 60 &&
                "complete"
              }
            />
          </div>
        </div>
      </VStack>
    </>
  );
};

export default CustomerInputForm;
